// lib/generation-task-status.ts
import type { GenerationTask } from '#types/types';
import { STUDIO_MODEL_REGISTRY, resolveRegistryModelIdFromRuntimeName, type StudioModelId } from '#lib/model-registry';

export type GenerationTaskStatus = 'running' | 'finished' | 'errored';

type StatusFields = Pick<GenerationTask, 'error' | 'isOutcomeReported'>;

/** The one definition the workspace task button, its menu and the version strip's live tiles share. */
export const generationTaskStatus = (task: StatusFields): GenerationTaskStatus => {
  if (task.error) return 'errored';
  return task.isOutcomeReported ? 'finished' : 'running';
};

export const classifyGenerationTasks = <T extends StatusFields>(tasks: readonly T[]): Record<GenerationTaskStatus, T[]> => {
  const groups: Record<GenerationTaskStatus, T[]> = { running: [], finished: [], errored: [] };
  tasks.forEach((task) => groups[generationTaskStatus(task)].push(task));
  return groups;
};

export const formatTaskElapsed = (seconds: number): string => {
  const totalSeconds = Math.max(0, Math.floor(seconds));
  return `${Math.floor(totalSeconds / 60)}:${String(totalSeconds % 60).padStart(2, '0')}`;
};

/** A running task counts up to now; a finished one ends when its output layer was built, unknown without one. */
export const generationTaskElapsedSeconds = (
  task: StatusFields & Pick<GenerationTask, 'startedAt' | 'layer'>,
  nowMs: number,
): number | null => {
  const startedAt = typeof task.startedAt === 'number' && Number.isFinite(task.startedAt) ? task.startedAt : null;
  if (startedAt === null) return null;
  if (generationTaskStatus(task) === 'running') return Math.max(0, (nowMs - startedAt) / 1000);
  const endedAt = task.layer?.timestamp;
  return typeof endedAt === 'number' && Number.isFinite(endedAt) && endedAt >= startedAt ? (endedAt - startedAt) / 1000 : null;
};

export const resolveGenerationTaskModel = (runtimeModel: unknown): { label: string; expectedSeconds?: number } => {
  const runtimeModelName = typeof runtimeModel === 'string' ? runtimeModel.trim() : '';
  const modelId = runtimeModelName ? resolveRegistryModelIdFromRuntimeName(runtimeModelName) : null;
  const modelDefinition = modelId && modelId in STUDIO_MODEL_REGISTRY
    ? STUDIO_MODEL_REGISTRY[modelId as StudioModelId]
    : null;
  return {
    label: modelDefinition?.name || runtimeModelName || 'Studio model',
    expectedSeconds: modelDefinition?.averageGenerationSeconds,
  };
};
